import { clamp } from '../utils/math-helpers.js';

function xpForNextLevel(level) {
  if (level < 16) return 2 * level + 7;
  if (level < 31) return 5 * level - 38;
  return 9 * level - 158;
}

const MINE_XP = {
  9: 1.0,
  10: 5.0,
  47: 2.0
};

const MOB_XP_TOTAL = {
  zombie: 5.0,
  skeleton: 5.0,
  skeleton_archer: 5.0,
  creeper: 5.0,
  spider: 5.0,
  enderman: 5.0,
  husk: 6.0,
  stray: 6.0,
  pillager: 6.5,
  raider_brute: 9.0,
  drowned: 6.0,
  ender_dragon: 220.0,
  cow: 2.0,
  sheep: 1.0,
  pig: 1.0,
  chicken: 1.0,
  goat: 1.6,
  wild_boar: 2.2,
  duck: 1.0,
  villager: 2.8
};
const MOB_HIT_XP_SHARE = 0.82;
const MOB_KILL_XP_SHARE = 0.18;

const SMELT_XP = {
  iron_ingot: 0.7,
  gold_ingot: 1.0,
  beef_cooked: 0.35,
  mutton_cooked: 0.35,
  pork_cooked: 0.35,
  chicken_cooked: 0.35,
  charcoal: 0.15
};

export function createExperienceSystem({
  t,
  onLevelUp = null
}) {
  let level = 0;
  let progress = 0;

  const labelEl = document.getElementById('xp-label');
  const fillEl = document.getElementById('xp-bar-fill');

  function updateUI() {
    const need = xpForNextLevel(level);
    const pct = clamp(progress / need, 0, 1);
    if (fillEl) fillEl.style.width = `${Math.round(pct * 100)}%`;
    if (labelEl) {
      labelEl.innerText = t('hud.xp_value', {
        level,
        current: Math.floor(progress),
        need
      });
    }
  }

  function addExperience(amount) {
    let remaining = Number(amount) || 0;
    if (remaining <= 0) return false;

    let leveledUp = false;
    while (remaining > 0.000001) {
      const need = xpForNextLevel(level);
      const left = need - progress;
      const step = Math.min(left, remaining);
      progress += step;
      remaining -= step;

      if (progress >= need - 0.000001) {
        level += 1;
        progress = 0;
        leveledUp = true;
        if (typeof onLevelUp === 'function') onLevelUp(level);
      }
    }

    updateUI();
    return leveledUp;
  }

  function onMineBlock(blockId) {
    addExperience(MINE_XP[blockId] || 0);
  }

  function getMobHitExperience(type, damage = 0, maxHp = 1) {
    const total = Math.max(0, Number(MOB_XP_TOTAL[type]) || 0);
    if (total <= 0) return 0;
    const dealt = Math.max(0, Number(damage) || 0);
    const hp = Math.max(1, Number(maxHp) || 1);
    const ratio = clamp(dealt / hp, 0, 1);
    return total * MOB_HIT_XP_SHARE * ratio;
  }

  function getMobKillBonusExperience(type, cause = 'player') {
    if (cause !== 'player') return 0;
    const total = Math.max(0, Number(MOB_XP_TOTAL[type]) || 0);
    if (total <= 0) return 0;
    return total * MOB_KILL_XP_SHARE;
  }

  function onMobKilled(type, cause) {
    const bonus = getMobKillBonusExperience(type, cause);
    if (bonus <= 0) return;
    addExperience(bonus);
  }

  function onTakeSmeltOutput(itemId, count = 1) {
    if (!itemId || count <= 0) return;
    addExperience((SMELT_XP[itemId] || 0) * count);
  }

  function refreshLocale() {
    updateUI();
  }

  updateUI();

  return {
    addExperience,
    onMineBlock,
    onMobKilled,
    getMobHitExperience,
    getMobKillBonusExperience,
    onTakeSmeltOutput,
    refreshLocale,
    exportState() {
      return {
        level,
        progress
      };
    },
    importState(snapshot = null) {
      const nextLevel = Number(snapshot?.level);
      const nextProgress = Number(snapshot?.progress);
      if (Number.isFinite(nextLevel)) {
        level = Math.max(0, Math.floor(nextLevel));
      }
      if (Number.isFinite(nextProgress)) {
        const need = xpForNextLevel(level);
        progress = clamp(nextProgress, 0, Math.max(0, need - 0.000001));
      }
      updateUI();
    },
    getLevel() {
      return level;
    },
    getProgress() {
      return progress;
    },
    getNeedForNextLevel() {
      return xpForNextLevel(level);
    }
  };
}
